import { SubmitHelpers } from '@area2k/use-form';
import useModal from '@area2k/use-modal';
import { isEqual } from 'lodash';
import { useState } from 'react';

import Button from '@/components/Button';
import Card from '@/components/Card';
import ConfirmationModal from '@/components/ConfirmationModal';
import FormElement from '@/components/FormElement';
import Modal from '@/components/Modal';
import Option from '@/components/Option';
import Stack from '@/components/Stack';
import Form from '@/form';
import FormColumns from '@/form/FormColumns';
import MaskedInputField from '@/form/MaskedInputField';
import TextField from '@/form/TextField';
import TextSelectField from '@/form/TextSelectField';
import {
  useUpdateUserProfileMutation,
  useWorkerUpdateProfileMutation,
} from '@/graphql';
import {
  GenderEnum,
  LanguageEnum,
  SmartphoneTypeEnum,
  TaxTypeEnum,
  UserRoleEnum,
} from '@/types/graphql';
import { CONFIRM_MSG } from '@/util/constants';
import { handleMutationFormError } from '@/util/error';
import { WorkerDetail } from '@/services/workersV2API';

type Props = {
  worker: WorkerDetail;
  onClose: () => void;
  onSuccess: () => void;
};

type FormValues = {
  email: string;
  firstName: string;
  middleName: string | null;
  lastName: string;
  gender: GenderEnum;
  heardFrom: string | null;
  language: LanguageEnum;
  phoneNumber: string;
  ownsCar: boolean;
  secondaryLanguage: LanguageEnum | null;
  smartphoneType: SmartphoneTypeEnum;
  dateOfBirth: string;
  hasSsn: boolean;
};

const smartPhoneTypeOptions = [
  { label: 'Android', value: SmartphoneTypeEnum.ANDROID },
  { label: 'IOS', value: SmartphoneTypeEnum.IOS },
  { label: 'Other', value: SmartphoneTypeEnum.OTHER },
];

const genderOptions = [
  { label: 'Male', value: GenderEnum.MALE },
  { label: 'Female', value: GenderEnum.FEMALE },
  { label: 'Non-Binary', value: GenderEnum.NON_BINARY },
  { label: 'Unspecified', value: GenderEnum.UNSPECIFIED },
];

const UpdateProfileModal = ({ worker, onClose, onSuccess }: Props) => {
  const initialValues: FormValues = {
    email: worker.user?.email || '',
    firstName: worker.user?.first_name || '',
    middleName: worker.user?.middle_name || null,
    lastName: worker.user?.last_name || '',
    gender: (worker.gender as GenderEnum) || GenderEnum.UNSPECIFIED,
    heardFrom: worker.heard_from || null,
    dateOfBirth: worker.user?.date_of_birth
      ? new Date(worker.user.date_of_birth).toISOString().split('T')[0]
      : '',
    language: (worker.user?.language as LanguageEnum) || LanguageEnum.ENGLISH,
    phoneNumber: worker.user?.phone_number || '',
    ownsCar: worker.owns_car || false,
    secondaryLanguage:
      (worker.user?.secondary_language as LanguageEnum) || null,
    smartphoneType:
      (worker.user?.smartphone_type as SmartphoneTypeEnum) ||
      SmartphoneTypeEnum.ANDROID,
    hasSsn: worker.has_ssn || false,
  };

  const [workerValues, setWorkerValues] = useState<FormValues>(initialValues);
  const [loading, setLoading] = useState(false);

  const [updateWorkerProfile, { loading: isLoading }] =
    useWorkerUpdateProfileMutation();
  const [updateUserProfile, { loading: isUserLoading }] =
    useUpdateUserProfileMutation();

  const handleWorkerValuesChange = (
    { value }: { value: any },
    id: keyof FormValues,
  ) => setWorkerValues({ ...workerValues, [id]: value });

  const [showConfirmationModal, hideConfirmationModal] = useModal(
    ({ values, submitHelpers }: any) => (
      <ConfirmationModal
        acceptAction={() => handleSubmit(values, submitHelpers)}
        acceptButtonLabel="Confirm"
        bodyContentText={CONFIRM_MSG}
        denyAction={() => {}}
        denyButtonLabel="Cancel"
        hideModal={hideConfirmationModal}
        title="Confirmation"
        loading={isLoading || isUserLoading || loading}
      />
    ),
    [workerValues, initialValues],
  );

  const handleSubmit = async (
    values: FormValues,
    { setFormError }: SubmitHelpers,
  ) => {
    setLoading(true);
    try {
      if (isEqual(workerValues, initialValues)) {
        return onClose();
      }

      const {
        dateOfBirth,
        email,
        firstName,
        middleName,
        lastName,
        gender,
        heardFrom,
        language,
        phoneNumber,
        secondaryLanguage,
        smartphoneType,
      } = values;

      const { ownsCar, hasSsn } = workerValues;
      const wid = String(worker.id);

      await updateWorkerProfile({
        variables: {
          workerId: wid,
          firstName,
          middleName,
          lastName,
          email,
          dateOfBirth: dateOfBirth!,
          gender,
          heardFrom,
          language,
          ownsCar,
          secondaryLanguage,
          smartphoneType,
          hasSsn,
        },
      });

      if (phoneNumber !== initialValues.phoneNumber) {
        await updateUserProfile({
          // @ts-ignore
          variables: {
            userId: String(worker.user?.id),
            firstName,
            middleName,
            lastName,
            phoneNumber,
            roleId: UserRoleEnum.WORKER,
          },
        });
      }

      onSuccess();
      onClose();
    } catch (err) {
      handleMutationFormError(err, {
        setFormError,
        errorMap: {
          all: (gqlError) => ({
            title: gqlError.name,
            message: gqlError.message,
            status: 'danger',
          }),
        },
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      disableClickout
      size="lg"
      title="Update Profile"
      onRequestClose={onClose}
    >
      <Card.Section>
        <Form
          initialValues={initialValues}
          onSubmit={async (values: FormValues, submitHelpers: SubmitHelpers) =>
            showConfirmationModal({ values, submitHelpers })
          }
        >
          <FormColumns layout="triple">
            <TextField
              autoFocus
              required
              callback={(fieldContext) => {
                handleWorkerValuesChange(fieldContext, 'firstName');
              }}
              fieldId="firstName"
              label="First Name"
            />
            <TextField
              callback={(fieldContext) => {
                handleWorkerValuesChange(fieldContext, 'middleName');
              }}
              fieldId="middleName"
              label="Middle Name"
            />
            <TextField
              required
              callback={(fieldContext) => {
                handleWorkerValuesChange(fieldContext, 'lastName');
              }}
              fieldId="lastName"
              label="Last Name"
            />
          </FormColumns>
          <FormColumns>
            <TextField
              required
              callback={(fieldContext) => {
                handleWorkerValuesChange(fieldContext, 'dateOfBirth');
              }}
              fieldId="dateOfBirth"
              label="Date of Birth"
              type="date"
            />
            <TextSelectField
              required
              callback={(fieldContext) => {
                handleWorkerValuesChange(fieldContext, 'gender');
              }}
              fieldId="gender"
              label="Gender"
              options={genderOptions}
            />
          </FormColumns>
          <FormColumns>
            <MaskedInputField
              callback={(fieldContext) => {
                handleWorkerValuesChange(fieldContext, 'phoneNumber');
              }}
              fieldId="phoneNumber"
              incompletemessage="Must be a valid phone number"
              label="Phone Number"
              mask="(000) 000-0000"
              placeholder={
                workerValues.phoneNumber
                  ? workerValues.phoneNumber
                  : '(555) 555-5555'
              }
              type="tel"
            />
            <TextField
              required
              callback={(fieldContext) => {
                handleWorkerValuesChange(fieldContext, 'email');
              }}
              fieldId="email"
              label="Email Address"
            />
          </FormColumns>
          <FormColumns>
            <TextSelectField
              required
              callback={(fieldContext) => {
                handleWorkerValuesChange(fieldContext, 'smartphoneType');
              }}
              fieldId="smartphoneType"
              label="Phone Type"
              options={smartPhoneTypeOptions}
            />
            <FormElement label="Important Information">
              <Stack vertical>
                <Option
                  appearance="checkbox"
                  checked={
                    worker.tax_type === 'w2' ? false : workerValues.hasSsn
                  }
                  disabled={worker.tax_type === 'w2'}
                  id="hasSsn"
                  label="Eligible for W2 employment"
                  onChange={() =>
                    handleWorkerValuesChange(
                      { value: !workerValues.hasSsn },
                      'hasSsn',
                    )
                  }
                />
                <Option
                  appearance="checkbox"
                  checked={workerValues.ownsCar}
                  id="ownsCar"
                  label="Access To Reliable Transportation?"
                  onChange={() =>
                    handleWorkerValuesChange(
                      { value: !workerValues.ownsCar },
                      'ownsCar',
                    )
                  }
                />
              </Stack>
            </FormElement>
          </FormColumns>
          <Stack justify="end">
            <Button
              a11yLabel="Submit form"
              id="btn-submit"
              isLoading={isLoading || isUserLoading || loading}
              label="Save"
              type="submit"
            />
          </Stack>
        </Form>
      </Card.Section>
    </Modal>
  );
};

export default UpdateProfileModal;
